import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings, settingsImageUrl } from "../context/SettingsContext";
import { Leaf, Loader2 } from "lucide-react";

const DEFAULT_BG = "https://images.pexels.com/photos/36853837/pexels-photo-36853837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, user } = useAuth();
  const { settings, text } = useSettings();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(username, password);
    setLoading(false);
    if (res.ok) navigate("/", { replace: true });
    else setError(res.error);
  };

  const appName = settings?.app_name || "OrtoFrutta";
  const slogan = settings?.slogan || "Manager";
  const logoUrl = settingsImageUrl("logo_file_id", settings);
  const bgUrl = settingsImageUrl("login_bg_file_id", settings) || DEFAULT_BG;

  return (
    <div className="dark min-h-screen flex bg-slate-950">
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url('${bgUrl}')` }}
      >
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display font-black text-4xl text-slate-50 leading-tight tracking-tight">
            {text("login_hero_title")}
          </h2>
          <p className="text-slate-300 mt-3 max-w-md">
            {text("login_hero_subtitle")}
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center overflow-hidden">
              {logoUrl ? <img src={logoUrl} alt="logo" className="w-full h-full object-cover" /> : <Leaf className="w-6 h-6 text-slate-950" />}
            </div>
            <div>
              <div className="font-display font-black text-2xl text-slate-50 tracking-tight">{appName}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{slogan}</div>
            </div>
          </div>

          <h1 className="font-display font-black text-3xl text-slate-50 mb-1">{text("login_heading")}</h1>
          <p className="text-slate-400 text-sm mb-6">{text("login_subtitle")}</p>

          <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Nickname</label>
          <input
            data-testid="login-username"
            type="text"
            autoCapitalize="none"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2.5 text-slate-100 outline-none focus:border-primary mb-4"
            placeholder="es. marco"
          />

          <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Password</label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2.5 text-slate-100 outline-none focus:border-primary mb-4"
            placeholder="••••••••"
          />

          {error && (
            <p data-testid="login-error" className="text-red-400 text-sm mb-4">{error}</p>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-slate-950 font-semibold rounded-md py-2.5 hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Accedi
          </button>
        </form>
      </div>
    </div>
  );
}
